import { jwtDecode } from "jwt-decode";

let refreshPromise = null;

export function logout() {
    window.localStorage.removeItem("authToken");
    window.localStorage.removeItem("refreshToken");
}

export function authenticate(credentials) {

    const URL_LOGIN = `${process.env.REACT_APP_API_URL}/api/login`

    return fetch(URL_LOGIN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(credentials),
    })
        .then(async response => {
            if (!response.ok) {
                const data = await response.json().catch(() => null);
                const error = new Error(data?.message || "Authentication failed");
                error.status = response.status;
                throw error;
            }
            return response.json();
        })
        .then(data => {
            const decoded = jwtDecode(data.token);
            if (decoded.role !== "ROLE_EMPLOYEE" && decoded.role !== "ROLE_FLEET_MANAGER") {
                const error = new Error("ROLE_NOT_ALLOWED");
                error.roleNotAllowed = true;
                throw error;
            }
            window.localStorage.setItem("authToken", data.token);
            window.localStorage.setItem("refreshToken", data.refresh_token);
            return true;
        });
}

function refreshAuthToken() {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
        try {
            const refreshToken = window.localStorage.getItem("refreshToken");
            if (!refreshToken) throw new Error("No refresh token");

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/token/refresh`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });

            if (!response.ok) throw new Error("Token refresh failed");

            const data = await response.json();
            window.localStorage.setItem("authToken", data.token);
            window.localStorage.setItem("refreshToken", data.refresh_token);
            return data.token;
        } finally {
            refreshPromise = null;
        }
    })();

    return refreshPromise;
}

export async function authFetch(url, options = {}) {
    let token = window.localStorage.getItem("authToken");

    // If token is expired, refresh before making the request
    if (token) {
        const { exp } = jwtDecode(token);
        if (exp <= Date.now() / 1000) {
            try {
                token = await refreshAuthToken();
            } catch {
                logout();
                window.location.href = "/login";
                throw new Error("Session expired");
            }
        }
    }

    const response = await fetch(url, {
        ...options,
        headers: {
            ...options.headers,
            ...(token ? { Authorization: "Bearer " + token } : {}),
        },
    });

    // If 401, try refreshing once then retry
    if (response.status === 401) {
        try {
            token = await refreshAuthToken();
        } catch {
            logout();
            window.location.href = "/login";
            throw new Error("Session expired");
        }
        return fetch(url, {
            ...options,
            headers: {
                ...options.headers,
                Authorization: "Bearer " + token,
            },
        });
    }

    return response;
}

export function setup() {
    const token = window.localStorage.getItem("authToken");
    if (token) {
        const decoded = jwtDecode(token);
        const { id, username, role, firstName, lastName, companyId, exp: expiration } = decoded;
        const refreshToken = window.localStorage.getItem("refreshToken");
        if (refreshToken || expiration > new Date().getTime() / 1000) {
            return { id, username, role, firstName, lastName, companyId };
        }
    }
}

export function forgotPassword(email) {
    return fetch(`${process.env.REACT_APP_API_URL}/api/password/forgot`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
    }).then(response => {
        if (!response.ok) {
            throw new Error("Échec de la demande");
        }
    });
}

export function resetPassword(token, plainPassword) {
    return fetch(`${process.env.REACT_APP_API_URL}/api/password/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, plainPassword }),
    }).then(response => {
        if (!response.ok) {
            const error = new Error("Échec de la réinitialisation");
            error.status = response.status;
            throw error;
        }
    });
}

export function setPassword(token, password) {
    return fetch(`${process.env.REACT_APP_API_URL}/api/invitations/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, plainPassword: password }),
    }).then(response => {
        if (!response.ok) {
            throw new Error("Échec de la définition du mot de passe");
        }
        return response.json();
    });
}

export function isAuthenticated() {
    const refreshToken = window.localStorage.getItem("refreshToken");
    if (refreshToken) return true;
    const token = window.localStorage.getItem("authToken");
    if (token) {
        const { exp: expiration } = jwtDecode(token);
        if (expiration > new Date().getTime() / 1000) {
            return true;
        }
    }
    return false;
}
